import { Button, Descriptions, Modal, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import IApplicationEmail from '~Api/ApplicationEmail/IApplicationEmail';
import RecipientTypeEnum from '~Api/ApplicationEmail/RecipientTypeEnum';
import { IGlobalState } from '~reducer';
import ModalStateEnum from '~UI/ModalStateEnum';
import { applicationEmailTextGetAction } from './actions';
import './emails.less';
import AttachmentsModal from './AttachmentsModal';
import PersonLink from './PersonLink';
import RecipientsModal from './RecipientsModal';
import recipientTypeLabels from './recipientTypeLabels';
import {
    applicationEmailSelector,
    applicationEmailTextSelector,
} from './selectors';

export default function ApplicationEmailModal(props: {
    applicationEmailUuid: string;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { applicationEmailUuid, isOpen, onCancel } = props;

    const [ attachmentsModalState, setAttachmentsModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ recipientsModalState, setRecipientsModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ recipientType, setRecipientType ] = useState<RecipientTypeEnum>(RecipientTypeEnum.To);

    const email: IApplicationEmail = useSelector((state: IGlobalState): IApplicationEmail => applicationEmailSelector(state, applicationEmailUuid));
    const emailText: string = useSelector((state: IGlobalState): string => applicationEmailTextSelector(state, applicationEmailUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (isOpen) {
            dispatch(applicationEmailTextGetAction(applicationEmailUuid));
        }
    }, [
        applicationEmailUuid,
        dispatch,
        isOpen,
    ]);

    useEffect((): void => {
        if (!isOpen) {
            setAttachmentsModalState(ModalStateEnum.Off);
            setRecipientsModalState(ModalStateEnum.Off);
        }
    }, [ isOpen ]);

    const onOpenAttachments: () => void = useCallback((): void => setAttachmentsModalState(ModalStateEnum.Open), []);
    const onCloseAttachments: () => void = useCallback((): void => setAttachmentsModalState(ModalStateEnum.Closed), []);
    const onViewTo: () => void = useCallback((): void => {
        setRecipientType(RecipientTypeEnum.To);
        setRecipientsModalState(ModalStateEnum.Open);
    }, []);
    const onViewCc: () => void = useCallback((): void => {
        setRecipientType(RecipientTypeEnum.Cc);
        setRecipientsModalState(ModalStateEnum.Open);
    }, []);
    const onCloseRecipients: () => void = useCallback((): void => setRecipientsModalState(ModalStateEnum.Closed), []);

    const toRecipients: IApplicationEmail['recipients'] = _.filter(email.recipients, (recipient: IApplicationEmail['recipients'][number]): boolean => recipient.type === RecipientTypeEnum.To);
    const ccRecipients: IApplicationEmail['recipients'] = _.filter(email.recipients, (recipient: IApplicationEmail['recipients'][number]): boolean => recipient.type === RecipientTypeEnum.Cc);

    const toSummary: string = _.join(_.map(toRecipients, (recipient: IApplicationEmail['recipients'][number]): string => recipient.name || recipient.email), ', ');
    const ccSummary: string = _.join(_.map(ccRecipients, (recipient: IApplicationEmail['recipients'][number]): string => recipient.name || recipient.email), ', ');
    const attachmentsSummary: string = _.join(_.map(email.attachments, (attachment: IApplicationEmail['attachments'][number]): string => attachment.filename), ', ');
    const toViewBlock: ReactElement = toRecipients.length > 0 && (
        <Button
            onClick={onViewTo}
            type='link'
        >
            View
        </Button>
    );
    const ccViewBlock: ReactElement = ccRecipients.length > 0 && (
        <Button
            onClick={onViewCc}
            type='link'
        >
            View
        </Button>
    );

    const attachmentsModalBlock: ReactElement = attachmentsModalState !== ModalStateEnum.Off && (
        <AttachmentsModal
            attachments={email.attachments}
            isOpen={attachmentsModalState === ModalStateEnum.Open}
            onCancel={onCloseAttachments}
        />
    );
    const recipientsModalBlock: ReactElement = recipientsModalState !== ModalStateEnum.Off && (
        <RecipientsModal
            isOpen={recipientsModalState === ModalStateEnum.Open}
            onCancel={onCloseRecipients}
            recipients={recipientType === RecipientTypeEnum.To ? toRecipients : ccRecipients}
            recipientType={recipientType}
        />
    );

    const attachmentsBlock: ReactElement = email.attachments.length > 0 && (
        <Descriptions
            bordered={true}
            className='attachments'
            column={1}
            size='small'
        >
            <Descriptions.Item
                label='Attachments'
            >
                <span className='summary'>{attachmentsSummary}</span>
                <Button
                    onClick={onOpenAttachments}
                    type='link'
                >
                    View
                </Button>
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <Modal
            className='application-emails-email-modal'
            footer={false}
            onCancel={onCancel}
            open={isOpen}
            title={email.subject || '-'}
            width={900}
        >
            <Descriptions
                bordered={true}
                column={1}
                size='small'
            >
                <Descriptions.Item
                    className='date'
                    label='Date'
                >
                    {dayjs(email.sentTime).format('dddd, D MMMM YYYY [at] h:mma')}
                </Descriptions.Item>
                <Descriptions.Item
                    className='from'
                    label='From'
                >
                    <PersonLink
                        email={email.senderEmail}
                        name={email.senderName}
                    />
                </Descriptions.Item>
                <Descriptions.Item
                    className='to'
                    label={recipientTypeLabels[RecipientTypeEnum.To]}
                >
                    <span className='summary'>{toSummary || '-'}</span>
                    {toViewBlock}
                </Descriptions.Item>
                <Descriptions.Item
                    className='cc'
                    label={recipientTypeLabels[RecipientTypeEnum.Cc]}
                >
                    <span className='summary'>{ccSummary || '-'}</span>
                    {ccViewBlock}
                </Descriptions.Item>
            </Descriptions>
            <Spin
                spinning={typeof emailText !== 'string'}
                wrapperClassName='content'
            >
                <Typography.Paragraph>{emailText}</Typography.Paragraph>
            </Spin>
            {attachmentsBlock}
            {attachmentsModalBlock}
            {recipientsModalBlock}
        </Modal>
    );
}
